<template>
    <div v-if="this.shouldBeVisible" class="cancel-order-popup-container">
        <modal
            ref="cancelOrderModal"
            id="cancelOrderPopup"
            size="modal-lg"
            :title="$t('order.cancelOrder')"
            :centered="true"
            :scrollable="true"
            :show="cancelOrderModal.show"
            :close="cancelOrderModal.hide"
            :closeable="cancelOrderModal.closeable"
            :tighter-close-button="true"
            @modal-opening="onModalOpening"
            @modal-visible="onResize"
            @modal-close="onModalClosing"
        >
            <form
                method="post"
                action=""
                enctype="multipart/form-data"
                @submit="onFormSubmit($event)"
                class="cancel-order-popup-form"
                :style="formStyle"
            >
                <div ref="cancelOrderModalBody" class="modal-body" :style="bodyStyle">
                    <div>
                        <div
                            v-if="response.show && typeof response.message === 'string' && response.message.length > 0"
                        >
                            <div
                                class="alert"
                                :class="{
                                    'alert-success': response.success,
                                    'alert-danger': !response.success,
                                }"
                            >
                                <p class="mb-0">{{ response.message }}</p>
                            </div>
                        </div>
                        <div>
                            <p v-if="modalText !== null" class="text-muted" v-html="modalText"></p>
                            <div class="text-center mt-1">
                                <button
                                    type="submit"
                                    style="font-size: 1rem"
                                    class="btn btn-primary btn-sm"
                                    :disabled="runningRequest"
                                >
                                    <i
                                        v-if="runningRequest"
                                        class="mdi mdi-loading mdi-spin"
                                        style="margin-right: 5px"
                                    ></i>
                                    <span v-html="$t('order.cancel_button')"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </modal>
    </div>
</template>

<script>
export default {
    props: {
        isPaymentConfirmed: {
            type: Boolean,
            required: true,
        },
        cancelOrderConfig: {
            type: Object,
            required: true,
            default() {
                return {
                    submitUri: null,
                };
            },
        },
        order: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            cancelOrderModal: {
                show: false,
                hide: false,
                closeable: true,
            },
            formStyle: {
                height: '100%',
            },
            bodyStyle: {
                height: '100%',
            },
            confirmError: false,
            confirm: false,
            licensePlate: {
                showForm: false,
                country: this.defaultLicensePlateCountry,
                value: this.order.license,
                updated: false,
            },
            formErrors: {},
            changedInputs: {
                country_code: false,
                license: false,
                special_license_plate_confirmed: true,
            },
            hasTemplateForm: false,
            runningRequest: false,
            response: {
                show: false,
                success: false,
                message: null,
            },
        };
    },

    computed: {
        /**
         * Determine if the setup popup of order should be visible.
         *
         * @returns {Boolean}
         */
        shouldBeVisible() {
            return this.order.order_insurance && this.isPaymentConfirmed;
        },

        /**
         * Load the modal text if defined.
         *
         * @returns {String|null}
         * */
        modalText() {
            const text = this.$t('cancelOrderPopup.modalText');

            return text.trim().length > 0 ? text : null;
        },
    },

    methods: {
        /**
         * Handle the visibility update of the modal.
         *
         * @returns {this}
         */
        showModal() {
            this.cancelOrderModal.show = true;
            this.cancelOrderModal.hide = false;
            this.cancelOrderModal.closeable = true;

            return this;
        },

        hideModal() {
            this.cancelOrderModal.show = false;
            this.cancelOrderModal.hide = true;
            this.cancelOrderModal.closeable = true;

            return this;
        },

        /**
         * Handle the event callback of opening modal.
         *
         * @returns {void}
         */
        onModalOpening() {
            this.$el.ownerDocument.body.classList.add('overflow-hidden');
        },

        /**
         * Handle the event callback for closing modal.
         *
         * @returns {void}
         */
        onModalClosing() {
            this.$el.ownerDocument.body.classList.remove('overflow-hidden');
            this.hideModal();
        },

        /**
         * Handle the submit callback of form to send the expected request.
         *
         * @param {SubmitEvent} $event
         *
         * @returns {void}
         */
        onFormSubmit($event) {
            $event.preventDefault();

            // In the case the form was already submitted, we have to prevent another submit.
            if (this.runningRequest) {
                return;
            }

            // Update the is submitting flag and reset the response alert.
            this.runningRequest = true;
            this.formErrors = {};
            this.response.show = false;
            this.response.success = false;
            this.response.message = null;
            this.confirmError = false;

            // Prepare the request
            const formData = new FormData();
            const uri = this.cancelOrderConfig.submitUri;

            axios
                .post(uri, formData)
                .then((response) => {
                    // In the case the request was failed for some reason, we have to apply
                    // the message to our response property to show the user more details why
                    // the request was failed.
                    this.response.success = response.data.success;
                    this.response.message = response.data.message;

                    if (!this.response.success) {
                        // In the case that the response contains more details about the exact reason
                        // of failure, we have to provide them by adding these to the formErrors property.
                        if (typeof response.data.formErrors === 'object') {
                            // Step through the keys to check if the input of those was changed or not.
                            for (const key in response.data.formErrors) {
                                if (typeof this.changedInputs[key] === 'undefined' || !this.changedInputs[key]) {
                                    delete response.data.formErrors[key];
                                }
                            }

                            // After the check above, we can assign the errors to the property.
                            this.formErrors = response.data.formErrors;
                        }

                        // Finally, disable the request blocking and show the response message in
                        // an alert container.
                        this.response.show = true;
                        this.runningRequest = false;

                        return;
                    }

                    // Show the success message and reload the page after 2 seconds, to assure
                    // we will show an updated page view with all necessary data.
                    this.response.show = true;

                    // Remove closeable
                    this.cancelOrderModal.closeable = false;

                    setTimeout(() => {
                        window.location.href = location.href;
                    }, 2000);
                })
                .catch(() => {
                    this.runningRequest = false;
                });
        },

        /**
         * Handle the show of license plate change form.
         *
         * @param {PointerEvent} $event
         *
         * @returns {void}
         */
        onShowLicensePlateChange($event) {
            $event.preventDefault();

            if (this.sendingRequest) {
                return;
            }

            this.licensePlate.showForm = true;
            this.confirm = false;
            this.$nextTick(() => {
                this.onResize();
            });
        },

        /**
         * Handle the hide of license plate change form.
         *
         * @param {PointerEvent} $event
         *
         * @returns {void}
         */
        onAbortLicensePlateChange($event) {
            $event.preventDefault();

            if (this.sendingRequest) {
                return;
            }

            this.licensePlate.showForm = false;
            this.licensePlate.country = this.order.country_code;
            this.licensePlate.value = this.order.license;
            this.$nextTick(() => {
                this.onResize();
            });
        },

        /**
         * Handle the callback of resize event, to assure the header size will be tracked correctly.
         *
         * @returns {void}
         */
        onResize() {},
    },

    beforeUnmount() {
        window.removeEventListener('resize', () => {
            this.onResize();
        });
    },

    beforeMount() {
        window.addEventListener('resize', () => {
            this.onResize();
        });
    },

    mounted() {
        this.hideModal();
    },
};
</script>
<style lang="scss">
.cancel-order-popup-container {
    @media screen and (max-width: 767px) {
        .modal-dialog {
            min-height: unset;
            max-height: unset;
            margin: 0;

            .modal-content {
                width: calc(100vw - 0.5rem);
            }
        }
    }
    @media screen and (min-width: 768px) {
        .horizontal-centered {
            margin: 0 auto;
        }
    }

    .modal-header {
        padding-bottom: 0;
    }
    .modal-body {
        padding-top: 0.5rem;
    }
}
</style>
